import { Colors, Img, Lightning, Registry, Utils } from "@lightningjs/sdk";
import theme from "../../../../lib/theme";
import configs from "../../../../lib/configs";
import { BrandingModel, MediaModel } from "../../../../lib/models";
import { MENU_WIDTH } from "../../../../lib/utils";

interface MediaDynamicBannerTemplateSpec extends Lightning.Component.TemplateSpec {
    Background: {
        BannerPlaceholder: object;
        MovieBackdrop: {
            Placeholder: object;
            Backdrops: {
                Backdrop: object;
                BackdropHelper: object;
            };
            BackgroundHalf: object;
            GradientLeft: object;
        };
    };
}

export class MediaDynamicBanner
    extends Lightning.Component<MediaDynamicBannerTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MediaDynamicBannerTemplateSpec>
{
    _isFullScreen = false;
    _timeout: undefined | number;
    _lastSrc: undefined | string;
    _index = 0;
    // _imgWidth = theme.banner.h * 1.77;
    _imgWidth = configs.banner.heightRatio ? theme.banner.h * 1.77 : theme.banner.w - theme.banner.content.w;

    _boundEventHandlers: any = {};

    readonly Background = this.getByRef("Background")!;
    readonly MovieBackdrop = this.Background.getByRef("MovieBackdrop")!;
    readonly GradientLeft = this.MovieBackdrop.getByRef("GradientLeft")!;
    readonly BackgroundHalf = this.MovieBackdrop.getByRef("BackgroundHalf")!;

    readonly Placeholder = this.MovieBackdrop.getByRef("Placeholder")!;
    readonly Backdrops = this.MovieBackdrop.getByRef("Backdrops")!;
    readonly Backdrop = this.Backdrops.getByRef("Backdrop")!;
    readonly BackdropHelper = this.Backdrops.getByRef("BackdropHelper")!;

    static override _template(): Lightning.Component.Template<MediaDynamicBannerTemplateSpec> {
        return {
            Background: {
                alpha: 1,
                w: theme.banner.w - MENU_WIDTH,
                h: configs.banner.fullScreen ? theme.layout.h : theme.banner.h,
                rect: true,
                clipping: true,
                color: Colors(theme.colors.bg.primary).get(),
                BannerPlaceholder: {
                    w: (w) => w,
                    h: (h) => h,
                    alpha: 1,
                    src: configs.banner.hasStaticBanner ? Utils.asset("images/media-banner.png") : undefined
                },
                MovieBackdrop: {
                    w: (w) => w,
                    h: (h) => h,
                    Placeholder: {
                        w: (300 / Math.max(theme.mediaItem.w - 40, theme.mediaItem.w - 40)) * theme.mediaItem.w - 40,
                        h: (300 / Math.max(theme.mediaItem.w - 40, theme.mediaItem.w - 40)) * theme.mediaItem.w - 40,
                        x: theme.banner.w - 600 - theme.banner.padding.x,
                        y: (h) => h / 2,
                        mountY: 0.5,
                        alpha: 0.01,
                        src: !configs.banner.omitPreview ? Utils.asset("images/placeholder.png") : undefined
                    },
                    Backdrops: {
                        alpha: 0,
                        w: configs.banner.useCoverUrl ? 300 : (w) => w,
                        h: configs.banner.useCoverUrl ? 300 : (h) => h,
                        x: configs.banner.useCoverUrl ? theme.banner.content.w + 450 : 0,
                        y: (h) => h / 2,
                        zIndex: configs.banner.useCoverUrl ? 10 : 0,
                        mountY: 0.5,
                        Backdrop: {
                            w: (w) => w,
                            h: (h) => h,
                            alpha: 0.01
                            // src: Utils.asset("images/background.png")
                        },
                        BackdropHelper: {
                            w: (w) => w,
                            h: (h) => h,
                            alpha: 0.01
                            // src: Utils.asset("images/background.png")
                        }
                    },
                    BackgroundHalf: {
                        x: 0,
                        y: 0,
                        w: (w) => w,
                        h: (h) => h,
                        rect: true,
                        color: Colors(theme.colors.bg.primary).alpha(1).get(),
                        alpha: 0
                    },
                    GradientLeft: {
                        x: 0,
                        y: 0,
                        w: (w) => w,
                        h: (h) => h,
                        rect: true,
                        colorLeft: Colors(theme.colors.gradient.start).get(),
                        colorRight: Colors(theme.colors.gradient.end).alpha(0.01).get()
                    }
                }
            }
        };
    }

    override _setup() {
        this._boundEventHandlers = {
            updateBackground: this._updateBackground.bind(this),
            hideBackground: this._hideBackground.bind(this),
            showBackground: this._showBackground.bind(this),
            clearBackground: this._clearBackground.bind(this)
        };
    }

    override _attach() {
        this.application.on("updateSelectedMovie", this._boundEventHandlers.updateBackground);
        this.application.on("hideBackground", this._boundEventHandlers.hideBackground);
        this.application.on("showBackground", this._boundEventHandlers.showBackground);
        this.application.on("clearBackground", this._boundEventHandlers.clearBackground);
    }

    override _detach() {
        this.application.off("updateSelectedMovie", this._boundEventHandlers.updateBackground);
        this.application.off("hideBackground", this._boundEventHandlers.hideBackground);
        this.application.off("showBackground", this._boundEventHandlers.showBackground);
        this.application.off("clearBackground", this._boundEventHandlers.clearBackground);
    }

    override _init() {
        this.Backdrop.on("txLoaded", () => {
            this.Backdrops.setSmooth("alpha", 1);
            this.Backdrop.setSmooth("alpha", 1);
            this.BackdropHelper.setSmooth("alpha", 0);

            if (!configs.banner.useCoverUrl) {
                this.BackgroundHalf.patch({
                    smooth: {
                        alpha: this._isFullScreen ? 0 : 1
                    }
                });
                this.GradientLeft.patch({
                    smooth: {
                        alpha: this._isFullScreen ? 0 : 1
                    }
                });
            }
        });
        this.BackdropHelper.on("txLoaded", () => {
            this.Backdrops.setSmooth("alpha", 1);
            this.BackdropHelper.setSmooth("alpha", 1);
            this.Backdrop.setSmooth("alpha", 0);

            if (!configs.banner.useCoverUrl) {
                this.BackgroundHalf.patch({
                    smooth: {
                        alpha: this._isFullScreen ? 0 : 1
                    }
                });
                this.GradientLeft.patch({
                    smooth: {
                        alpha: this._isFullScreen ? 0 : 1
                    }
                });
            }
        });

        for (const child of this.Backdrops.children) {
            child?.on("txError", () => {
                if (this._lastSrc && child.src && child.src.indexOf(this._lastSrc) > -1) {
                    this.Placeholder.setSmooth("alpha", 1);
                    this.Backdrops.setSmooth("alpha", 0);

                    if (!configs.banner.useCoverUrl) {
                        if (this._isFullScreen) {
                            this.GradientLeft.patch({
                                smooth: {
                                    alpha: 0
                                }
                            });

                            this.BackgroundHalf.patch({
                                smooth: {
                                    alpha: 0
                                }
                            });

                            child.patch({
                                w: (w) => w
                            });
                        } else {
                            this.GradientLeft.patch({
                                smooth: {
                                    alpha: 1
                                }
                            });

                            this.BackgroundHalf.patch({
                                smooth: {
                                    alpha: 1
                                }
                            });

                            this._setLimitedWidth();
                        }
                    }
                }
            });
        }

        if (!configs.banner.useCoverUrl) {
            this._setLimitedWidth();
        }
    }

    _setLimitedWidth() {
        const x = this._getXOffset();

        for (const child of this.Backdrops.children) {
            child?.patch({
                w: this._imgWidth,
                x: x
            });
        }

        this._adjustGradientPosition();
    }

    _adjustGradientPosition() {
        const x = configs.banner.fullScreen ? 140 : theme.layout.w - MENU_WIDTH - this._imgWidth;

        this.GradientLeft.patch({
            x: x,
            w: (w) => w - x
        });

        this.BackgroundHalf.patch({
            w: x
        });
    }

    _getXOffset() {
        return configs.banner.fullScreen || this._isFullScreen ? 0 : theme.layout.w - MENU_WIDTH - this._imgWidth;
    }

    _clearBackground() {
        this._lastSrc = undefined;
        this.MovieBackdrop.patch({
            alpha: 0
        });
        this.Backdrop.patch({
            src: undefined,
            alpha: 0.01
        });
        this.BackdropHelper.patch({
            src: undefined,
            alpha: 0.01
        });
    }

    _hideBackground() {
        this.Background.patch({
            smooth: {
                alpha: 0
            }
        });
    }

    _showBackground() {
        this.MovieBackdrop.patch({
            alpha: 1
        });
        this.Background.patch({
            alpha: 1
        });
    }

    _updateBackground(movie: MediaModel | BrandingModel) {
        const prevFullscreen = this._isFullScreen;
        this._isFullScreen = "backdropWideUrl" in movie && movie.type === "banner";
        const url = this._isFullScreen ? (movie as BrandingModel).backdropWideUrl : movie.backdropUrl;

        if (!configs.banner.hasStaticBanner) {
            if ((prevFullscreen && !this._isFullScreen) || !this._lastSrc) {
                this.BackgroundHalf.patch({
                    alpha: this._isFullScreen ? 0 : 1
                });
                this.GradientLeft.patch({
                    alpha: this._isFullScreen ? 0 : 1
                });
            }
        }

        this.MovieBackdrop.patch({
            alpha: 1
        });

        if (!url) {
            this.Backdrops.setSmooth("alpha", 0);
            this.Placeholder.setSmooth("alpha", 1);

            this._lastSrc = undefined;
            return;
        }

        if (this._timeout) {
            Registry.clearTimeout(this._timeout);
            this._timeout = undefined;
        }

        if (url === this._lastSrc) return;

        this._timeout = Registry.setTimeout(
            () => {
                this._timeout = undefined;

                this._lastSrc = url;

                if (!configs.banner.omitPreview) {
                    if (configs.banner.useCoverUrl) {
                        this.Backdrops.children[this._index]?.patch({
                            texture: Img(`${this._lastSrc}`).cover(300, 300),
                            alpha: 0.01
                        });
                    } else {
                        const w =
                            configs.banner.fullScreen || this._isFullScreen
                                ? theme.banner.w - theme.menu.w
                                : this._imgWidth;

                        this.Backdrops.children[this._index]?.patch({
                            texture: Img(
                                `${this._lastSrc}${
                                    configs.appId != "skyvod"
                                        ? `?width=${
                                              configs.banner.fullScreen || this._isFullScreen
                                                  ? theme.banner.w
                                                  : Math.floor(w / 2)
                                          }`
                                        : `/${Math.floor(w)}x${theme.banner.h}_fit.jpg`
                                }`
                            ).cover(w, configs.banner.fullScreen ? theme.layout.h : theme.banner.h),
                            x: this._isFullScreen ? 0 : this._getXOffset(),
                            w: w,
                            alpha: 0.01
                        });
                    }
                }

                this._index ^= 1;

                this.Backdrops.setSmooth("alpha", 1);
                if (!configs.banner.hasStaticBanner) {
                    this.Placeholder.setSmooth("alpha", 0.01);
                }
            },
            this._lastSrc ? 250 : 0
        );
    }
}
